import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Check,
  RotateCcw,
  Activity,
  ShieldCheck,
  AlertTriangle,
  Siren,
  ClipboardList,
  Save,
  ChevronDown,
  FileText,
  Plus,
  Clock,
  User,
  Loader2,
  AlertCircle,
  Printer,
} from "lucide-react";
import { createFallScaleApi } from "./api/fallScaleApi";
import authHeader from "../../../../../authentication/authservices/auth-header";
import { baseUrl } from "../../../../../http-common";

/**
 * Morse Fall Scale (Modified) — master-driven.
 *
 * All scale content (factors, options, bands, interventions) is loaded from the
 * backend `/config` endpoint. Saved assessments are read from and written to the
 * backend. Score and band are authoritative on the server; the live readout here
 * mirrors that math from the same master data.
 *
 * Props:
 *   patient        : { mrn, name, ward, age, sex }  — from your HIMS patient master
 *   apiBaseUrl     : API root (default "/api/fall-scale")
 *   getAuthHeaders : optional () => ({ Authorization: "Bearer ..." })
 *   onSaved        : optional (detail) => void
 */

const DEMO_PATIENT = {
  mrn: "UHID-48273",
  name: "Ananya Sharma",
  ward: "Med-2 / Bed 14",
  age: 72,
  sex: "Female",
};

const BAND_ICON = { NONE: ShieldCheck, LOW: AlertTriangle, HIGH: Siren };
function bandIcon(code) { return BAND_ICON[code] || ShieldCheck; }

function formatWhen(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function MorseFallScale({
  patient,
  apiBaseUrl = `${baseUrl}/fall-scale`,
  getAuthHeaders = authHeader,
  onSaved,
}) {
  console.log("patientpatientpatientpatientpatientpatientpatient", patient);
  const userInfomation = JSON.parse(localStorage?.getItem("userInfo"))
  console.log("userInfomationuserInfomationuserInfomation", userInfomation);

  useEffect(() => {
    if (userInfomation) {
      setDecl((prev) => ({
        ...prev,
        nurse: userInfomation.userName,
      }));
    }
  }, []);
  const api = useMemo(
    () => createFallScaleApi({ baseUrl: apiBaseUrl, getAuthHeaders }),
    [apiBaseUrl, getAuthHeaders]
  );

  // Master config
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Working assessment
  const [view, setView] = useState("form");
  const [selections, setSelections] = useState({});   // { [factorId]: optionId }
  const [checked, setChecked] = useState({});          // { [interventionId]: bool }
  const [decl, setDecl] = useState({ nurse: "", relative: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Saved records
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});          // { [id]: detail }
  const [lastSavedId, setLastSavedId] = useState(null);
  const [pdfBusyId, setPdfBusyId] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  /* ---------------- data loading ---------------- */

  const loadConfig = useCallback(() => {
    setConfigError(null);
    api.getConfig().then(setConfig).catch((e) => setConfigError(e.message));
  }, [api]);

  const loadRecords = useCallback(() => {
    setRecordsLoading(true);
    api.listAssessments(patient?.mrn)
      .then((rows) => setRecords(rows || []))
      .catch(() => setRecords([]))
      .finally(() => setRecordsLoading(false));
  }, [api, patient?.mrn]);

  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  /* ---------------- derived scoring ---------------- */

  const optionValue = useMemo(() => {
    const m = {};
    config?.factors.forEach((f) => f.options.forEach((o) => { m[o.id] = o.value; }));
    return m;
  }, [config]);

  const overallMax = useMemo(() => {
    if (!config) return 125;
    return config.factors.reduce(
      (s, f) => s + (f.options.length ? Math.max(...f.options.map((o) => o.value)) : 0), 0
    );
  }, [config]);

  const score = useMemo(
    () => Object.values(selections).reduce((s, optId) => s + (optionValue[optId] || 0), 0),
    [selections, optionValue]
  );

  const band = useMemo(
    () => config?.bands.find((b) => score >= b.minScore && score <= b.maxScore) || null,
    [config, score]
  );

  const bandInterventions = band?.interventions || [];
  const doneCount = bandInterventions.filter((iv) => checked[iv.id]).length;

  const completed = config ? config.factors.filter((f) => selections[f.id] !== undefined).length : 0;
  const total = config ? config.factors.length : 0;
  const allDone = total > 0 && completed === total;
  const canSave = allDone && decl.nurse.trim().length > 0 && !saving;

  /* ---------------- actions ---------------- */

  function resetForm() {
    setSelections({});
    setChecked({});
    setDecl({ nurse: "", relative: "" });
    setSaveError(null);
  }

  async function handleSave() {
    if (!canSave || !config) return;
    setSaving(true);
    setSaveError(null);
    const payload = {
      patient: {
        mrn: patient.mrn, name: patient.name, ward: patient.ward,
        age: patient.age, gender: patient.gender, bedNo: patient?.bedNo
      },
      answers: config.factors.map((f) => ({ factorId: f.id, optionId: selections[f.id] })),
      interventions: bandInterventions.map((iv) => ({ interventionId: iv.id, completed: !!checked[iv.id] })),
      assessedBy: decl.nurse.trim(),
      relativeName: decl.relative.trim() || null,
    };
    try {
      const detail = await api.createAssessment(payload);
      setDetails((d) => ({ ...d, [detail.id]: detail }));
      setLastSavedId(detail.id);
      onSaved?.(detail);
      resetForm();
      setView("list");
      setExpanded(detail.id);
      loadRecords();
    } catch (e) {
      setSaveError([e.message, ...(e.details || [])].filter(Boolean).join(" · "));
    } finally {
      setSaving(false);
    }
  }

  async function openPdf(id) {
    setPdfError(null);
    setPdfBusyId(id);
    try {
      const blob = await api.getAssessmentPdf(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setPdfError(`Could not generate PDF: ${e.message}`);
    } finally {
      setPdfBusyId(null);
    }
  }

  function toggleExpand(id) {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next && !details[id]) {
      api.getAssessment(id)
        .then((d) => setDetails((cache) => ({ ...cache, [id]: d })))
        .catch(() => { });
    }
  }

  /* ---------------- render ---------------- */

  return (
    <div className="mfs">
      <style>{css}</style>
      <div className="mfs-shell">
        {/* <header className="mfs-head">
          <div className="mfs-head-l">
            <div className="mfs-mark"><Activity size={18} strokeWidth={2.4} /></div>
            <div>
              <h1>Morse Fall Scale<span className="mfs-mod"> · Modified</span></h1>
              <p className="mfs-tag">Fall Risk Assessment · complete all items</p>
            </div>
          </div>
          <div className="mfs-brand">Smarter Data. Better Care.</div>
        </header> */}

        {/* <section className="mfs-patient">
          <InfoPair label="Patient name" value={patient.name} />
          <InfoPair label="MRN / UHID" value={patient.mrn} />
          <InfoPair label="Ward / Bed" value={patient.ward} />
          <InfoPair label="Age · Sex" value={[patient.age, patient.sex].filter(Boolean).join(" · ")} />
        </section> */}

        <div className="mfs-tabs">
          <button className={view === "form" ? "is-active" : ""} onClick={() => setView("form")}>
            <FileText size={14} strokeWidth={2.3} /> New Assessment
          </button>
          <button className={view === "list" ? "is-active" : ""} onClick={() => setView("list")}>
            <ClipboardList size={14} strokeWidth={2.3} /> Saved Records
            <span className="mfs-tab-count">{records.length}</span>
          </button>
        </div>

        {view === "form" ? (
          configError ? (
            <StateBox icon={<AlertCircle size={26} />} title="Couldn’t load the assessment"
              note={configError} action={<button className="mfs-newbtn" onClick={loadConfig}>Retry</button>} />
          ) : !config ? (
            <StateBox icon={<Loader2 size={26} className="mfs-spin" />} title="Loading assessment…" />
          ) : (
            <FormView
              config={config} score={score} band={band} overallMax={overallMax}
              completed={completed} total={total} allDone={allDone}
              selections={selections} setSelections={setSelections}
              bandInterventions={bandInterventions} doneCount={doneCount}
              checked={checked} setChecked={setChecked}
              decl={decl} setDecl={setDecl}
              canSave={canSave} saving={saving} saveError={saveError} onSave={handleSave} onReset={resetForm}
            />
          )
        ) : (
          <ListView
            records={records} loading={recordsLoading} expanded={expanded}
            details={details} onToggle={toggleExpand} lastSavedId={lastSavedId}
            goForm={() => setView("form")}
            onPdf={openPdf} pdfBusyId={pdfBusyId} pdfError={pdfError}
          />
        )}
      </div>
    </div>
  );
}

/* ---------------- Form view ---------------- */

function FormView(props) {
  const {
    config, score, band, overallMax, completed, total, allDone,
    selections, setSelections, bandInterventions, doneCount,
    checked, setChecked, decl, setDecl, canSave, saving, saveError, onSave, onReset,
  } = props;

  const accent = band?.color || "#0E9F6E";
  const accentSoft = band?.softColor || "#E6F6EF";
  const BandIcon = bandIcon(band?.code);

  // meter zones derived from bands
  const bands = config.bands;
  const zones = bands.map((b, i) => {
    const left = b.minScore;
    const right = i < bands.length - 1 ? bands[i + 1].minScore : overallMax;
    return { color: b.color, basis: ((right - left) / overallMax) * 100 };
  });
  const scaleLabels = [0, ...bands.slice(1).map((b) => b.minScore), overallMax];
  const markerPct = Math.min(score, overallMax) / overallMax;

  return (
    <>
      <section className="mfs-gauge" style={{ "--accent": accent, "--accent-soft": accentSoft }}>
        <div className="mfs-gauge-readout">
          <div className="mfs-score">
            <span className="mfs-score-num">{score}</span>
            <span className="mfs-score-max">/ {overallMax}</span>
          </div>
          {band && (
            <div className="mfs-band">
              <span className="mfs-band-pill"><BandIcon size={15} strokeWidth={2.4} />{band.name}</span>
              <span className="mfs-band-action">{band.action}</span>
            </div>
          )}
        </div>

        <div className="mfs-meter-wrap">
          <div className="mfs-meter">
            {zones.map((z, i) => (
              <div key={i} className="mfs-zone" style={{ flexBasis: `${z.basis}%`, background: z.color }} />
            ))}
            <div className="mfs-marker" style={{ left: `${markerPct * 100}%` }}><span className="mfs-knob" /></div>
          </div>
          <div className="mfs-scale">
            {scaleLabels.map((n, i) => <span key={i}>{n}</span>)}
          </div>
        </div>

        <div className="mfs-progress">
          {completed} of {total} items scored
          {!allDone && <em> — score is provisional</em>}
        </div>
      </section>

      <section className="mfs-section">
        <h2 className="mfs-h2"><span className="mfs-h2-no">1</span>Fall Risk Assessment</h2>
        <div className="mfs-factors">
          {config.factors.map((f) => (
            <FactorRow key={f.id} factor={f} value={selections[f.id]}
              onSelect={(optId) => setSelections({ ...selections, [f.id]: optId })} />
          ))}
        </div>
      </section>

      <section className="mfs-section">
        <h2 className="mfs-h2"><span className="mfs-h2-no">2</span>Risk Interpretation</h2>
        <div className="mfs-bands">
          {config.bands.map((b) => {
            const active = band && b.code === band.code;
            const Icon = bandIcon(b.code);
            const rangeText = b.maxScore >= overallMax ? `≥ ${b.minScore}` : `${b.minScore} – ${b.maxScore}`;
            return (
              <div key={b.id} className={`mfs-bandcard${active ? " is-active" : ""}`}
                style={active ? { "--accent": b.color, "--accent-soft": b.softColor } : undefined}>
                <div className="mfs-bandcard-top">
                  <Icon size={16} strokeWidth={2.3} style={{ color: b.color }} />
                  <span className="mfs-bandcard-range">{rangeText}</span>
                  {active && <span className="mfs-bandcard-now">Current</span>}
                </div>
                <div className="mfs-bandcard-name" style={{ color: b.color }}>{b.name}</div>
                <div className="mfs-bandcard-action">{b.action}</div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mfs-section">
        <h2 className="mfs-h2"><span className="mfs-h2-no">3</span>Nursing Interventions</h2>
        {band ? (
          <div className="mfs-interv" style={{ "--accent": accent, "--accent-soft": accentSoft }}>
            <div className="mfs-interv-head">
              <ClipboardList size={16} strokeWidth={2.3} />
              <span>{band.name} — {band.action}</span>
              <span className="mfs-interv-count">{doneCount}/{bandInterventions.length}</span>
            </div>
            <ul className="mfs-checklist">
              {bandInterventions.map((iv) => {
                const on = !!checked[iv.id];
                return (
                  <li key={iv.id}>
                    <label className={`mfs-check${on ? " is-on" : ""}`}>
                      <input type="checkbox" checked={on}
                        onChange={() => setChecked({ ...checked, [iv.id]: !on })} />
                      <span className="mfs-box">{on && <Check size={13} strokeWidth={3.2} />}</span>
                      <span className="mfs-check-text">{iv.text}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="mfs-muted-note">Interventions appear once items are scored.</div>
        )}
      </section>

      <section className="mfs-section">
        <h2 className="mfs-h2"><span className="mfs-h2-no">4</span>Nurse Declaration</h2>
        <div className="mfs-decl">
          <Field label="Assessed by (nurse)" value={decl.nurse}
            onChange={(v) => setDecl({ ...decl, nurse: v })} placeholder="Name & ID" />
          <Field label="Patient’s relative" value={decl.relative}
            onChange={(v) => setDecl({ ...decl, relative: v })} placeholder="Name" />
        </div>
      </section>

      {saveError && (
        <div className="mfs-error-banner"><AlertCircle size={15} strokeWidth={2.3} />{saveError}</div>
      )}

      <footer className="mfs-foot">
        <div className="mfs-foot-actions">
          <button className="mfs-save" disabled={!canSave} onClick={onSave}>
            {saving ? <Loader2 size={15} strokeWidth={2.3} className="mfs-spin" /> : <Save size={15} strokeWidth={2.3} />}
            {saving ? "Saving…" : "Save assessment"}
          </button>
          <button className="mfs-reset" onClick={onReset} disabled={saving}>
            <RotateCcw size={15} strokeWidth={2.3} /> Reset
          </button>
        </div>
        <span className="mfs-foot-note">
          {canSave ? "Ready to save" : "Score all items and enter the assessing nurse to save"}
        </span>
      </footer>
    </>
  );
}

/* ---------------- List view ---------------- */

function ListView({ records, loading, expanded, details, onToggle, lastSavedId, goForm, onPdf, pdfBusyId, pdfError }) {
  return (
    <section className="mfs-section">
      {lastSavedId && (
        <div className="mfs-saved-banner"><Check size={15} strokeWidth={3} /> Assessment saved to records.</div>
      )}
      {pdfError && (
        <div className="mfs-error-banner"><AlertCircle size={15} strokeWidth={2.3} />{pdfError}</div>
      )}

      <div className="mfs-list-head">
        <h2 className="mfs-h2" style={{ margin: 0 }}>
          <span className="mfs-h2-no"><ClipboardList size={13} strokeWidth={2.6} /></span>Saved Records
        </h2>
        <button className="mfs-newbtn" onClick={goForm}><Plus size={14} strokeWidth={2.6} /> New assessment</button>
      </div>

      {loading ? (
        <StateBox icon={<Loader2 size={26} className="mfs-spin" />} title="Loading records…" />
      ) : records.length === 0 ? (
        <div className="mfs-empty">
          <FileText size={30} strokeWidth={1.6} />
          <b>No saved assessments yet</b>
          Complete the form and save to build this patient’s fall-risk history.
        </div>
      ) : (
        <ul className="mfs-records">
          {records.map((r) => {
            const open = expanded === r.id;
            const Icon = bandIcon(r.bandCode);
            return (
              <li key={r.id} className={`mfs-record${r.id === lastSavedId ? " is-new" : ""}`}>
                <button className="mfs-record-row" onClick={() => onToggle(r.id)}>
                  <span className="mfs-record-score" style={{ color: r.bandColor }}>{r.totalScore}</span>
                  <span className="mfs-record-band" style={{ background: r.bandSoftColor, color: r.bandColor }}>
                    <Icon size={13} strokeWidth={2.4} />{r.bandName}
                  </span>
                  <span className="mfs-record-meta">
                    <span className="mfs-record-when"><Clock size={13} strokeWidth={2.2} />{formatWhen(r.assessedAt)}</span>
                    <span className="mfs-record-by"><User size={13} strokeWidth={2.2} />{r.assessedBy || "—"}</span>
                    <span className="mfs-record-interv">{r.interventionsDone}/{r.interventionsTotal} interventions</span>
                  </span>
                  <ChevronDown size={18} strokeWidth={2.2} className={`mfs-record-chev${open ? " rot" : ""}`} />
                </button>
                {open && <RecordDetail detail={details[r.id]} onPdf={onPdf} pdfBusy={pdfBusyId === r.id} />}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function RecordDetail({ detail, onPdf, pdfBusy }) {
  if (!detail) {
    return <div className="mfs-detail"><div className="mfs-detail-loading"><Loader2 size={16} className="mfs-spin" /> Loading details…</div></div>;
  }
  const b = detail.band;
  return (
    <div className="mfs-detail">
      <div className="mfs-detail-bar">
        <button className="mfs-pdf-btn" onClick={() => onPdf(detail.id)} disabled={pdfBusy}>
          {pdfBusy ? <Loader2 size={14} strokeWidth={2.3} className="mfs-spin" /> : <Printer size={14} strokeWidth={2.3} />}
          {pdfBusy ? "Preparing…" : "Print / Download PDF"}
        </button>
      </div>
      <div className="mfs-detail-grid">
        {detail.answers.map((a) => (
          <div className="mfs-detail-item" key={a.factorId}>
            <span className="mfs-detail-q">{a.seqNo}. {a.factorLabel}</span>
            <span className="mfs-detail-a">{a.optionLabel}<em>{a.score}</em></span>
          </div>
        ))}
      </div>

      <div className="mfs-detail-band" style={{ background: b.softColor, color: b.color }}>
        Total {detail.totalScore} · {b.name} — {b.action}
      </div>

      <div className="mfs-detail-sub">Interventions ({detail.interventionsDone}/{detail.interventionsTotal} completed)</div>
      <ul className="mfs-detail-checks">
        {detail.interventions.map((iv) => (
          <li key={iv.interventionId} className={iv.completed ? "is-on" : ""}>
            <span className="mfs-detail-box">{iv.completed && <Check size={11} strokeWidth={3.4} />}</span>
            {iv.text}
          </li>
        ))}
      </ul>

      <div className="mfs-detail-decl">
        <div><span>Assessed by</span>{detail.assessedBy || "—"}</div>
        <div><span>Patient’s relative</span>{detail.relativeName || "—"}</div>
      </div>
    </div>
  );
}

/* ---------------- small pieces ---------------- */

function FactorRow({ factor, value, onSelect }) {
  const groupId = `mfs-${factor.id}`;
  return (
    <div className="mfs-factor" role="radiogroup" aria-labelledby={`${groupId}-lbl`}>
      <div className="mfs-factor-l">
        <span className="mfs-factor-no">{factor.seqNo}</span>
        <div>
          <div id={`${groupId}-lbl`} className="mfs-factor-name">{factor.label}</div>
          {factor.hint && <div className="mfs-factor-hint">{factor.hint}</div>}
        </div>
      </div>
      <div className="mfs-options">
        {factor.options.map((opt) => {
          const on = value === opt.id;
          return (
            <label key={opt.id} className={`mfs-opt${on ? " is-on" : ""}`}>
              <input type="radio" name={groupId} checked={on} onChange={() => onSelect(opt.id)} />
              <span className="mfs-opt-label">{opt.label}</span>
              <span className="mfs-opt-pts">{opt.value}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function InfoPair({ label, value }) {
  return (
    <div className="mfs-info">
      <span className="mfs-info-lbl">{label}</span>
      <span className="mfs-info-val">{value || "—"}</span>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <label className="mfs-field">
      <span className="mfs-field-lbl">{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function StateBox({ icon, title, note, action }) {
  return (
    <div className="mfs-statebox">
      <div className="mfs-statebox-icon">{icon}</div>
      <b>{title}</b>
      {note && <span className="mfs-statebox-note">{note}</span>}
      {action}
    </div>
  );
}

const css = `
.mfs * { box-sizing: border-box; }
.mfs {
  --navy-900:#08152B; --navy-800:#0C1F3F; --navy-700:#102A52;
  --teal:#16BFB0; --teal-d:#0FA89A;
  --text:#10243B; --muted:#5F7488;
  --line:#DBE5EE; --paper:#FFFFFF; --bg:#EEF3F7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: var(--text); -webkit-font-smoothing: antialiased; line-height: 1.45;
}
.mfs-shell { max-width: 880px; margin: 0 auto; padding: 18px; background: var(--bg); }
.mfs-spin { animation: mfs-spin 0.8s linear infinite; }
@keyframes mfs-spin { to { transform: rotate(360deg); } }

.mfs-head { display: flex; align-items: center; justify-content: space-between; gap: 16px;
  background: linear-gradient(120deg, var(--navy-900), var(--navy-700)); color: #fff;
  border-radius: 16px; padding: 18px 22px; }
.mfs-head-l { display: flex; align-items: center; gap: 14px; }
.mfs-mark { width: 38px; height: 38px; border-radius: 11px; flex: none; display: grid; place-items: center;
  color: #06141f; background: linear-gradient(135deg, var(--teal), #3ad6c8); box-shadow: 0 4px 14px rgba(22,191,176,.4); }
.mfs-head h1 { margin: 0; font-size: 19px; font-weight: 700; letter-spacing: -.2px; }
.mfs-mod { color: var(--teal); font-weight: 600; }
.mfs-tag { margin: 2px 0 0; font-size: 12.5px; color: #9db4cc; }
.mfs-brand { font-size: 11px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; color: var(--teal); white-space: nowrap; }

.mfs-patient { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-top: 14px; }
.mfs-info { display: flex; flex-direction: column; gap: 3px; background: var(--paper); border: 1px solid var(--line); border-radius: 10px; padding: 10px 13px; }
.mfs-info-lbl { font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); }
.mfs-info-val { font-size: 14px; font-weight: 600; color: var(--text); }

.mfs-tabs { display: flex; gap: 4px; margin-top: 14px; background: #E2EAF1; padding: 4px; border-radius: 11px; }
.mfs-tabs button { flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: none;
  background: transparent; color: var(--muted); font-weight: 600; font-size: 13px; padding: 9px 12px; border-radius: 8px;
  cursor: pointer; font-family: inherit; transition: background .15s, color .15s; }
.mfs-tabs button.is-active { background: #fff; color: var(--navy-800); box-shadow: 0 1px 4px rgba(10,30,55,.1); }
.mfs-tab-count { background: var(--teal); color: #042; font-size: 11px; font-weight: 700; border-radius: 999px; padding: 1px 7px; font-family: ui-monospace, monospace; }

.mfs-field { display: flex; flex-direction: column; gap: 5px; }
.mfs-field-lbl { font-size: 10.5px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); }
.mfs-field input { border: 1px solid var(--line); background: var(--paper); border-radius: 9px; padding: 9px 11px;
  font-size: 13.5px; color: var(--text); width: 100%; font-family: inherit; transition: border-color .15s, box-shadow .15s; }
.mfs-field input:focus { outline: none; border-color: var(--teal); box-shadow: 0 0 0 3px rgba(22,191,176,.18); }

.mfs-gauge { margin-top: 14px; background: var(--paper); border: 1px solid var(--line); border-radius: 16px; padding: 18px 22px 16px; border-top: 3px solid var(--accent); }
.mfs-gauge-readout { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.mfs-score { display: flex; align-items: baseline; gap: 7px; }
.mfs-score-num { font-family: ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, monospace; font-size: 52px; font-weight: 700; line-height: .9; color: var(--accent); font-variant-numeric: tabular-nums; letter-spacing: -1px; }
.mfs-score-max { font-family: ui-monospace, monospace; font-size: 16px; color: var(--muted); font-weight: 600; }
.mfs-band { text-align: right; }
.mfs-band-pill { display: inline-flex; align-items: center; gap: 6px; background: var(--accent-soft); color: var(--accent); font-weight: 700; font-size: 13px; padding: 5px 12px; border-radius: 999px; }
.mfs-band-action { display: block; font-size: 12px; color: var(--muted); margin-top: 5px; }
.mfs-meter-wrap { margin-top: 16px; }
.mfs-meter { position: relative; display: flex; height: 12px; border-radius: 999px; overflow: visible; background: #eef2f6; }
.mfs-zone:first-child { border-radius: 999px 0 0 999px; }
.mfs-zone:last-child { border-radius: 0 999px 999px 0; }
.mfs-zone { opacity: .32; }
.mfs-marker { position: absolute; top: 50%; transform: translate(-50%, -50%); transition: left .35s cubic-bezier(.4,0,.2,1); }
.mfs-knob { display: block; width: 18px; height: 18px; border-radius: 50%; background: #fff; border: 4px solid var(--accent); box-shadow: 0 2px 8px rgba(10,30,55,.28); }
.mfs-scale { display: flex; justify-content: space-between; margin-top: 7px; font-family: ui-monospace, monospace; font-size: 10.5px; color: var(--muted); }
.mfs-progress { margin-top: 12px; font-size: 12px; color: var(--muted); }
.mfs-progress em { font-style: normal; color: var(--accent); font-weight: 600; }

.mfs-section { margin-top: 22px; }
.mfs-h2 { display: flex; align-items: center; gap: 10px; margin: 0 0 12px; font-size: 13px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; color: var(--navy-800); }
.mfs-h2-no { width: 22px; height: 22px; border-radius: 7px; flex: none; display: grid; place-items: center; font-size: 12px; background: var(--navy-800); color: #fff; }

.mfs-factors { display: flex; flex-direction: column; gap: 8px; }
.mfs-factor { display: flex; align-items: center; justify-content: space-between; gap: 16px; background: var(--paper); border: 1px solid var(--line); border-radius: 13px; padding: 13px 15px; }
.mfs-factor-l { display: flex; align-items: center; gap: 12px; min-width: 0; }
.mfs-factor-no { width: 26px; height: 26px; border-radius: 8px; flex: none; display: grid; place-items: center; font-size: 12.5px; font-weight: 700; color: var(--teal-d); background: #E6F8F5; }
.mfs-factor-name { font-size: 14px; font-weight: 600; color: var(--text); }
.mfs-factor-hint { font-size: 11.5px; color: var(--muted); margin-top: 1px; }
.mfs-options { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
.mfs-opt { position: relative; display: inline-flex; align-items: center; gap: 8px; border: 1.5px solid var(--line); background: #fff; border-radius: 9px; padding: 7px 9px 7px 12px; cursor: pointer; font-size: 12.5px; transition: border-color .14s, background .14s, box-shadow .14s; }
.mfs-opt input { position: absolute; opacity: 0; pointer-events: none; }
.mfs-opt:hover { border-color: #B9C8D6; }
.mfs-opt-label { color: var(--text); font-weight: 500; }
.mfs-opt-pts { font-family: ui-monospace, monospace; font-size: 11.5px; font-weight: 700; color: var(--muted); background: #F1F5F8; border-radius: 6px; padding: 2px 7px; min-width: 26px; text-align: center; }
.mfs-opt.is-on { border-color: var(--teal); background: #E9FAF7; box-shadow: 0 0 0 3px rgba(22,191,176,.14); }
.mfs-opt.is-on .mfs-opt-label { color: var(--teal-d); font-weight: 700; }
.mfs-opt.is-on .mfs-opt-pts { background: var(--teal); color: #042; }
.mfs-opt:has(input:focus-visible) { box-shadow: 0 0 0 3px rgba(22,191,176,.35); }

.mfs-bands { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.mfs-bandcard { background: var(--paper); border: 1px solid var(--line); border-radius: 13px; padding: 13px 14px; transition: box-shadow .15s; }
.mfs-bandcard.is-active { border-color: var(--accent); background: var(--accent-soft); box-shadow: 0 6px 18px -8px var(--accent); }
.mfs-bandcard-top { display: flex; align-items: center; gap: 8px; }
.mfs-bandcard-range { font-family: ui-monospace, monospace; font-size: 12px; font-weight: 700; color: var(--text); }
.mfs-bandcard-now { margin-left: auto; font-size: 9.5px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--accent); background: rgba(255,255,255,.7); border-radius: 5px; padding: 2px 6px; }
.mfs-bandcard-name { font-size: 14.5px; font-weight: 700; margin-top: 8px; }
.mfs-bandcard-action { font-size: 11.5px; color: var(--muted); margin-top: 2px; }

.mfs-interv { background: var(--paper); border: 1px solid var(--line); border-radius: 14px; overflow: hidden; }
.mfs-interv-head { display: flex; align-items: center; gap: 9px; background: var(--accent-soft); color: var(--accent); padding: 11px 16px; font-weight: 700; font-size: 13px; border-bottom: 1px solid var(--line); }
.mfs-interv-count { margin-left: auto; font-family: ui-monospace, monospace; font-size: 12px; background: #fff; border-radius: 6px; padding: 2px 9px; color: var(--accent); }
.mfs-checklist { list-style: none; margin: 0; padding: 6px; }
.mfs-checklist li + li { border-top: 1px solid #F0F4F7; }
.mfs-check { display: flex; align-items: flex-start; gap: 11px; padding: 9px 10px; cursor: pointer; border-radius: 8px; transition: background .12s; }
.mfs-check:hover { background: #F6F9FB; }
.mfs-check input { position: absolute; opacity: 0; pointer-events: none; }
.mfs-box { width: 19px; height: 19px; flex: none; margin-top: 1px; border-radius: 6px; border: 1.5px solid #C2D0DC; background: #fff; display: grid; place-items: center; color: #fff; transition: all .14s; }
.mfs-check.is-on .mfs-box { background: var(--accent); border-color: var(--accent); }
.mfs-check-text { font-size: 13px; color: var(--text); }
.mfs-check.is-on .mfs-check-text { color: var(--muted); text-decoration: line-through; }
.mfs-check:has(input:focus-visible) { background: #EFF6FB; box-shadow: 0 0 0 2px rgba(22,191,176,.3) inset; }
.mfs-muted-note { background: var(--paper); border: 1px dashed var(--line); border-radius: 12px; padding: 16px; font-size: 13px; color: var(--muted); text-align: center; }

.mfs-decl { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

.mfs-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--line); flex-wrap: wrap; }
.mfs-foot-actions { display: flex; gap: 8px; }
.mfs-save { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; border: none; background: linear-gradient(135deg, var(--teal-d), var(--teal)); color: #04221f; font-weight: 700; font-size: 13px; padding: 10px 18px; border-radius: 9px; font-family: inherit; box-shadow: 0 4px 12px rgba(22,191,176,.35); transition: transform .1s; }
.mfs-save:hover:not(:disabled) { transform: translateY(-1px); }
.mfs-save:disabled { background: #CBD7E0; color: #fff; box-shadow: none; cursor: not-allowed; }
.mfs-reset { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; border: 1px solid var(--line); background: #fff; color: var(--navy-800); font-weight: 600; font-size: 13px; padding: 9px 15px; border-radius: 9px; font-family: inherit; transition: background .14s, border-color .14s; }
.mfs-reset:hover:not(:disabled) { background: #F4F8FB; border-color: #B9C8D6; }
.mfs-reset:disabled { opacity: .5; cursor: not-allowed; }
.mfs-foot-note { font-size: 11.5px; color: var(--muted); }

.mfs-error-banner { display: flex; align-items: center; gap: 8px; background: #FBE7E9; color: #B4232F; font-weight: 600; font-size: 12.5px; padding: 10px 14px; border-radius: 10px; margin-top: 18px; border: 1px solid #F3C6CB; }

.mfs-saved-banner { display: flex; align-items: center; gap: 8px; background: #E6F6EF; color: #0E9F6E; font-weight: 600; font-size: 13px; padding: 10px 14px; border-radius: 10px; margin-bottom: 14px; border: 1px solid #BFE8D6; }
.mfs-list-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.mfs-newbtn { display: inline-flex; align-items: center; gap: 6px; border: 1px solid var(--line); background: #fff; color: var(--navy-800); font-weight: 600; font-size: 12.5px; padding: 8px 13px; border-radius: 8px; cursor: pointer; font-family: inherit; transition: background .14s; }
.mfs-newbtn:hover { background: #F4F8FB; }
.mfs-empty, .mfs-statebox { text-align: center; padding: 40px 20px; color: var(--muted); background: var(--paper); border: 1px dashed var(--line); border-radius: 14px; display: flex; flex-direction: column; align-items: center; font-size: 13px; gap: 4px; }
.mfs-empty svg, .mfs-statebox-icon { color: #B7C6D3; margin-bottom: 8px; }
.mfs-empty b, .mfs-statebox b { color: var(--text); font-size: 14px; }
.mfs-statebox-note { max-width: 360px; }
.mfs-statebox .mfs-newbtn { margin-top: 10px; }

.mfs-records { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px; }
.mfs-record { background: var(--paper); border: 1px solid var(--line); border-radius: 13px; overflow: hidden; transition: box-shadow .2s; }
.mfs-record.is-new { box-shadow: 0 0 0 2px var(--teal); }
.mfs-record-row { width: 100%; display: flex; align-items: center; gap: 14px; flex-wrap: wrap; background: none; border: none; cursor: pointer; padding: 13px 15px; font-family: inherit; text-align: left; transition: background .12s; }
.mfs-record-row:hover { background: #F8FAFC; }
.mfs-record-score { font-family: ui-monospace, monospace; font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; min-width: 34px; }
.mfs-record-band { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; font-weight: 700; padding: 4px 11px; border-radius: 999px; }
.mfs-record-meta { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; flex: 1; }
.mfs-record-when, .mfs-record-by { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: var(--muted); }
.mfs-record-by { color: var(--text); font-weight: 500; }
.mfs-record-interv { font-size: 11.5px; color: var(--muted); font-family: ui-monospace, monospace; }
.mfs-record-chev { color: var(--muted); transition: transform .2s; margin-left: auto; }
.mfs-record-chev.rot { transform: rotate(180deg); }

.mfs-detail { padding: 4px 16px 16px; border-top: 1px solid var(--line); background: #FBFCFD; }
.mfs-detail-bar { display: flex; justify-content: flex-end; padding: 12px 0 2px; }
.mfs-pdf-btn { display: inline-flex; align-items: center; gap: 7px; cursor: pointer; border: 1px solid var(--line);
  background: #fff; color: var(--navy-800); font-weight: 600; font-size: 12px; padding: 7px 13px; border-radius: 8px;
  font-family: inherit; transition: background .14s, border-color .14s; }
.mfs-pdf-btn:hover:not(:disabled) { background: #F4F8FB; border-color: #B9C8D6; }
.mfs-pdf-btn:disabled { opacity: .6; cursor: not-allowed; }
.mfs-detail-loading { display: flex; align-items: center; gap: 8px; padding: 16px 0; font-size: 13px; color: var(--muted); }
.mfs-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 22px; margin: 12px 0; }
.mfs-detail-item { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; font-size: 12.5px; padding: 7px 0; border-bottom: 1px dashed #E4EBF1; }
.mfs-detail-q { color: var(--muted); }
.mfs-detail-a { font-weight: 600; color: var(--text); text-align: right; }
.mfs-detail-a em { font-style: normal; font-family: ui-monospace, monospace; color: var(--teal-d); margin-left: 7px; background: #E9FAF7; padding: 1px 6px; border-radius: 5px; font-size: 11px; }
.mfs-detail-band { font-size: 13px; font-weight: 700; padding: 9px 13px; border-radius: 9px; margin: 14px 0 4px; }
.mfs-detail-sub { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); margin: 14px 0 7px; }
.mfs-detail-checks { list-style: none; margin: 0 0 14px; padding: 0; display: flex; flex-direction: column; gap: 5px; }
.mfs-detail-checks li { display: flex; align-items: flex-start; gap: 9px; font-size: 12.5px; color: var(--muted); }
.mfs-detail-checks li.is-on { color: var(--text); }
.mfs-detail-box { width: 16px; height: 16px; flex: none; margin-top: 1px; border-radius: 5px; border: 1.5px solid #C2D0DC; background: #fff; display: grid; place-items: center; color: #fff; }
.mfs-detail-checks li.is-on .mfs-detail-box { background: #0E9F6E; border-color: #0E9F6E; }
.mfs-detail-decl { display: flex; gap: 28px; flex-wrap: wrap; padding-top: 12px; border-top: 1px solid var(--line); }
.mfs-detail-decl div { font-size: 13px; font-weight: 600; color: var(--text); }
.mfs-detail-decl span { display: block; font-size: 10px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--muted); margin-bottom: 2px; }

@media (max-width: 720px) {
  .mfs-patient { grid-template-columns: 1fr 1fr; }
  .mfs-bands { grid-template-columns: 1fr; }
  .mfs-decl { grid-template-columns: 1fr; }
  .mfs-detail-grid { grid-template-columns: 1fr; }
  .mfs-factor { flex-direction: column; align-items: stretch; gap: 11px; }
  .mfs-options { justify-content: flex-start; }
  .mfs-score-num { font-size: 44px; }
  .mfs-head { flex-direction: column; align-items: flex-start; gap: 10px; }
}
@media (prefers-reduced-motion: reduce) { .mfs-marker, .mfs-spin { transition: none; animation: none; } }
`;
